import React, { useState, useEffect } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import { Lock, Eye, EyeOff, Mail } from "lucide-react";
import NormalAlert from "../../Alerts/NormalAlert";
import LoginBg from "../../../Assets/Loginbg.svg";
import { resetPassword } from "../Services/authService";

const ResetPassword = () => {
  const navigate = useNavigate();
  const { uid } = useParams();
  const [errors, setErrors] = useState({});

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [alert, setAlert] = useState(null);
  const [email, setEmail] = useState("");

  const isStrongPassword = (password) => {
    return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).{8,}$/.test(
      password,
    );
  };

 const handleReset = async () => {
  if (!validateForm()) return;

  try {
    const res = await resetPassword(uid, email, newPassword);

    setAlert({
      type: "success",
      message: res.data.message,
    });

    setTimeout(() => {
      navigate("/");
    }, 1500);
  } catch (error) {
    setAlert({
      type: "fail",
      message:
        error?.response?.data?.message || "Something went wrong. Try again.",
    });
  }
};

  const validateForm = () => {
    let newErrors = {};

    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Invalid email format";
    }

    if (!newPassword.trim()) {
      newErrors.newPassword = "New password is required";
    } else if (!isStrongPassword(newPassword)) {
      newErrors.newPassword =
        "Password must be 8+ chars with uppercase, lowercase, number & special character";
    }

    if (!confirmPassword.trim()) {
      newErrors.confirmPassword = "Confirm password is required";
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };  

  return (
    <>
      <div className="min-h-screen flex flex-col md:flex-row animate-fadeIn">
        {/* LEFT SIDE */}
        <div className="relative w-full md:w-1/2 flex flex-col justify-start pt-[24vh] px-8 md:px-20 pb-10 bg-white">
          {/* Header */}
          <div className="absolute top-0 left-0 w-full h-[22vh] bg-gradient-to-br from-[#e3f2fd] via-[#bbdefb] to-[#90caf9] flex items-center justify-center">
            <Link to="/">
              <img
                src={process.env.PUBLIC_URL + "/HRSetu-final-logo.svg"}
                alt="HR Setu recruitment platform logo"
                className="w-28"
              />
            </Link>
          </div>

          {/* CONTENT */}
          <div className="relative z-10">
            <div className="text-center mb-6">
              <h4 className="text-xl font-semibold text-gray-800 mt-10">
                Reset Your Password
              </h4>
              <p className="text-gray-500">Enter your new password below</p>
            </div>

            {/* RESET FORM */}
            <div className="space-y-4 px-4 md:px-10 animate-fadeSlideUp">
              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email address
                </label>
                <div className="relative">
                  <Mail
                    className="absolute left-3 top-3 text-gray-400"
                    size={18}
                  />
                   <input
                    type="email"
                    className="w-full pl-10 pr-10 py-3 rounded-lg border bg-gray-50 focus:outline-none focus:ring-2 border-gray-300 focus:ring-blue-400"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setErrors((prev) => ({ ...prev, email: "" }));
                    }}
                  />

                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                  )}
                </div>
              </div>

              {/* New Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <div className="relative">
                  <Lock
                    className="absolute left-3 top-3 text-gray-400"
                    size={18}
                  />
                  <input
                    type={showPassword ? "text" : "password"}
                    className="w-full pl-10 pr-10 py-3 rounded-lg border bg-gray-50 focus:outline-none focus:ring-2 border-gray-300 focus:ring-blue-400"
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      setErrors((prev) => ({ ...prev, newPassword: "" }));
                    }}
                  />

                  {errors.newPassword && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.newPassword}
                    </p>
                  )}
                  <button
                    type="button"
                    className="absolute right-3 top-3 text-gray-500"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock
                    className="absolute left-3 top-3 text-gray-400"
                    size={18}
                  />
                  <input
                    type={showPassword ? "text" : "password"}
                    className="w-full pl-10 pr-10 py-3 rounded-lg border bg-gray-50 focus:outline-none focus:ring-2 border-gray-300 focus:ring-blue-400"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setErrors((prev) => ({ ...prev, confirmPassword: "" }));
                    }}
                  />

                  {errors.confirmPassword && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.confirmPassword}
                    </p>
                  )}
                </div>
              </div>

              {/* Button */}
              <div className="text-center mt-4">
                <button
                  onClick={handleReset}
                  className="w-[180px] h-[48px] bg-sky-500 text-white rounded-md font-semibold shadow-sm transition hover:bg-sky-600"
                >
                  Reset Password
                </button>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[85%] text-center text-xs text-gray-400 border-t border-gray-200 py-2">
            Designed & Developed by
            <span className="text-blue-500"> VRSamadhan Tech Solution</span>
          </div>
        </div>

        {/* RIGHT SIDE IMAGE */}
        <div className="hidden md:block md:w-1/2">
          <img
            src={LoginBg}
            alt="HR Setu login background showing modern workspace"
            className="w-full h-screen object-cover animate-slowZoom"
          />
        </div>
      </div>

      {/* ALERT */}
      {alert && (
        <NormalAlert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
    </>
  );
};

export default ResetPassword;
